import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  blockUser,
  getProfileConnectionsPage,
  getPublicProfile,
  getPublicProfileStats,
  getBlockState,
  getProfileStats,
  listBlockedUsers,
  searchProfileConnections,
  searchUserProfiles,
  getUserProfile,
  setFollow,
  unblockUser,
  subscribeToFollowState,
  updateUserProfile,
  uploadProfileImage,
  type ProfileConnection,
  type ProfileConnectionKind,
  type ProfileConnectionsCursor,
  type ProfileImageKind,
  type ProfileStats,
  type PublicProfile,
  type UpdateProfileInput,
} from '@/src/features/profile/profile-service';
import {
  isNetworkAvailable,
  isRecoverableNetworkError,
  queueSetFollowing,
  queueUpdateProfile,
} from '@/src/features/offline/outbox-service';
import { useQueuedFollowing } from '@/src/features/offline/use-outbox';
import type { ImagePickerAsset } from 'expo-image-picker';
import { assert } from '@/src/utils/assert';

const PROFILE_STATS_QUERY_VERSION = 'reaction-counts-v4-blocks';
const PROFILE_QUERY_STALE_TIME_MS = 5 * 60 * 1000;
const PROFILE_STATS_STALE_TIME_MS = 5 * 60 * 1000;

export function profileQueryKey(uid: string | undefined) {
  return ['profile', uid] as const;
}

export function profileStatsQueryKey(uid: string | undefined) {
  return ['profile-stats', uid, PROFILE_STATS_QUERY_VERSION] as const;
}

export function publicProfileQueryKey(uid: string | undefined) {
  return ['public-profile', uid] as const;
}

export function publicProfileStatsQueryKey(uid: string | undefined) {
  return ['public-profile-stats', uid, PROFILE_STATS_QUERY_VERSION] as const;
}

export function profileConnectionsQueryKey(
  uid: string | undefined,
  kind: ProfileConnectionKind | undefined,
) {
  return ['profile-connections', uid, kind] as const;
}

export function profileConnectionSearchQueryKey(
  uid: string | undefined,
  kind: ProfileConnectionKind | undefined,
  searchText: string,
) {
  return ['profile-connections-search', uid, kind, searchText] as const;
}

export function userProfileSearchQueryKey(searchText: string) {
  return ['user-profile-search', searchText] as const;
}

export function followStateQueryKey(
  currentUid: string | undefined,
  targetUid: string | undefined,
) {
  return ['follow-state', currentUid, targetUid] as const;
}

export function blockStateQueryKey(
  currentUid: string | undefined,
  targetUid: string | undefined,
) {
  return ['block-state', currentUid, targetUid] as const;
}

export function blockedUsersQueryKey(uid: string | undefined) {
  return ['blocked-users', uid] as const;
}

const FOLLOW_WRITE_TIMEOUT_MS = 8000;
const PROFILE_CONNECTIONS_PAGE_SIZE = 25;

type ProfileConnectionsState = {
  connections: ProfileConnection[];
  cursor: ProfileConnectionsCursor;
  error: Error | null;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
};

const initialProfileConnectionsState: ProfileConnectionsState = {
  connections: [],
  cursor: null,
  error: null,
  hasMore: false,
  loading: false,
  loadingMore: false,
};

async function withFollowWriteTimeout<T>(promise: Promise<T>) {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => {
          reject(new Error('Follow update timed out.'));
        }, FOLLOW_WRITE_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

export function useProfile(
  uid: string | undefined,
  options: {
    refetchOnMount?: boolean | 'always';
  } = {},
) {
  return useQuery({
    queryKey: profileQueryKey(uid),
    enabled: Boolean(uid),
    refetchOnMount: options.refetchOnMount,
    staleTime: PROFILE_QUERY_STALE_TIME_MS,
    queryFn: async () => {
      assert(uid, 'User ID is required to load a profile.');
      return getUserProfile(uid);
    },
  });
}

export function useUpdateProfile(uid: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateProfileInput) => {
      assert(uid, 'User ID is required to update a profile.');

      if (!(await isNetworkAvailable())) {
        await queueUpdateProfile({ input, uid });
        return 'queued' as const;
      }

      try {
        await updateUserProfile(uid, input);
      } catch (error) {
        if (isRecoverableNetworkError(error)) {
          await queueUpdateProfile({ input, uid });
          return 'queued' as const;
        }

        throw error;
      }

      return 'saved' as const;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: profileQueryKey(uid) }),
        queryClient.invalidateQueries({
          queryKey: publicProfileQueryKey(uid),
        }),
      ]);
    },
  });
}

export function useUpdateProfileImage(uid: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      asset,
      kind,
      onUploadProgress,
    }: {
      asset: ImagePickerAsset;
      kind: ProfileImageKind;
      onUploadProgress?: (progress: number) => void;
    }) => {
      assert(uid, 'User ID is required to update a profile image.');

      if (!(await isNetworkAvailable())) {
        await queueUpdateProfile({
          imageAsset: asset,
          imageKind: kind,
          input: {},
          uid,
        });
        return 'queued' as const;
      }

      try {
        const uploadResult = await uploadProfileImage({
          asset,
          kind,
          onUploadProgress,
          uid,
        });
        await updateUserProfile(
          uid,
          kind === 'avatar'
            ? {
                photoURL: uploadResult.downloadURL,
                profileImages: { avatar: uploadResult.profileImage },
              }
            : {
                coverPhotoURL: uploadResult.downloadURL,
                profileImages: { cover: uploadResult.profileImage },
              },
        );
        return uploadResult.downloadURL;
      } catch (error) {
        if (isRecoverableNetworkError(error)) {
          await queueUpdateProfile({
            imageAsset: asset,
            imageKind: kind,
            input: {},
            uid,
          });
          return 'queued' as const;
        }

        throw error;
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: profileQueryKey(uid) }),
        queryClient.invalidateQueries({
          queryKey: publicProfileQueryKey(uid),
        }),
      ]);
    },
  });
}

export function useProfileStats(uid: string | undefined) {
  return useQuery({
    queryKey: profileStatsQueryKey(uid),
    enabled: Boolean(uid),
    staleTime: PROFILE_STATS_STALE_TIME_MS,
    queryFn: async () => {
      assert(uid, 'User ID is required to load profile stats.');
      return getProfileStats(uid);
    },
  });
}

export function usePublicProfile(uid: string | undefined, enabled = true) {
  return useQuery({
    queryKey: publicProfileQueryKey(uid),
    enabled: Boolean(uid) && enabled,
    queryFn: async () => {
      assert(uid, 'User ID is required to load a public profile.');
      return getPublicProfile(uid);
    },
  });
}

export function usePublicProfileStats(uid: string | undefined, enabled = true) {
  return useQuery({
    queryKey: publicProfileStatsQueryKey(uid),
    enabled: Boolean(uid) && enabled,
    staleTime: PROFILE_STATS_STALE_TIME_MS,
    queryFn: async () => {
      assert(uid, 'User ID is required to load public profile stats.');
      return getPublicProfileStats(uid);
    },
  });
}

export function useBlockState(
  currentUid: string | undefined,
  targetUid: string | undefined,
) {
  return useQuery({
    queryKey: blockStateQueryKey(currentUid, targetUid),
    enabled: Boolean(currentUid && targetUid && currentUid !== targetUid),
    queryFn: async () => {
      assert(targetUid, 'Target user ID is required to load block state.');
      return getBlockState(targetUid);
    },
  });
}

export function useBlockedUsers(
  uid: string | undefined,
  {
    enabled = true,
    query = '',
  }: {
    enabled?: boolean;
    query?: string;
  } = {},
) {
  const normalizedQuery = query.trim();

  return useInfiniteQuery({
    queryKey: [...blockedUsersQueryKey(uid), normalizedQuery.toLowerCase()],
    enabled: Boolean(uid) && enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      listBlockedUsers({
        cursor: pageParam,
        query: normalizedQuery,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function useProfileConnections(
  uid: string | undefined,
  kind: ProfileConnectionKind | undefined,
  enabled = true,
) {
  const requestIdRef = useRef(0);
  const [state, setState] = useState<ProfileConnectionsState>(
    initialProfileConnectionsState,
  );

  const refetch = useCallback(async () => {
    if (!uid || !kind || !enabled) {
      setState(initialProfileConnectionsState);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setState({
      ...initialProfileConnectionsState,
      loading: true,
    });

    try {
      const page = await getProfileConnectionsPage({
        kind,
        limitCount: PROFILE_CONNECTIONS_PAGE_SIZE,
        uid,
      });

      if (requestIdRef.current !== requestId) {
        return;
      }

      setState({
        connections: page.connections,
        cursor: page.cursor,
        error: null,
        hasMore: page.hasMore,
        loading: false,
        loadingMore: false,
      });
    } catch (caughtError) {
      if (requestIdRef.current !== requestId) {
        return;
      }

      setState({
        ...initialProfileConnectionsState,
        error:
          caughtError instanceof Error
            ? caughtError
            : new Error('Unable to load profile connections.'),
      });
    }
  }, [enabled, kind, uid]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const fetchNextPage = useCallback(async () => {
    if (
      !uid ||
      !kind ||
      !enabled ||
      state.loading ||
      state.loadingMore ||
      !state.hasMore ||
      !state.cursor
    ) {
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const cursor = state.cursor;
    setState((currentState) => ({
      ...currentState,
      error: null,
      loadingMore: true,
    }));

    try {
      const page = await getProfileConnectionsPage({
        cursor,
        kind,
        limitCount: PROFILE_CONNECTIONS_PAGE_SIZE,
        uid,
      });

      if (requestIdRef.current !== requestId) {
        return;
      }

      setState((currentState) => {
        const existingUids = new Set(
          currentState.connections.map((connection) => connection.uid),
        );
        const nextConnections = page.connections.filter(
          (connection) => !existingUids.has(connection.uid),
        );

        return {
          connections: [...currentState.connections, ...nextConnections],
          cursor: page.cursor,
          error: null,
          hasMore: page.hasMore,
          loading: false,
          loadingMore: false,
        };
      });
    } catch (caughtError) {
      if (requestIdRef.current !== requestId) {
        return;
      }

      setState((currentState) => ({
        ...currentState,
        error:
          caughtError instanceof Error
            ? caughtError
            : new Error('Unable to load more profile connections.'),
        loadingMore: false,
      }));
    }
  }, [
    enabled,
    kind,
    state.cursor,
    state.hasMore,
    state.loading,
    state.loadingMore,
    uid,
  ]);

  return {
    data: state.connections,
    error: state.error,
    fetchNextPage,
    hasNextPage: state.hasMore,
    isError: Boolean(state.error),
    isFetchingNextPage: state.loadingMore,
    isLoading: state.loading,
    refetch,
  };
}

export function useProfileConnectionSearch({
  enabled = true,
  kind,
  searchText,
  uid,
}: {
  enabled?: boolean;
  kind: ProfileConnectionKind | undefined;
  searchText: string;
  uid: string | undefined;
}) {
  const trimmedSearchText = searchText.trim();

  return useQuery({
    queryKey: profileConnectionSearchQueryKey(uid, kind, trimmedSearchText),
    enabled:
      Boolean(uid) && Boolean(kind) && enabled && trimmedSearchText.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      assert(uid, 'User ID is required to search profile connections.');
      assert(
        kind,
        'Connection kind is required to search profile connections.',
      );

      return searchProfileConnections({
        kind,
        limitCount: 50,
        searchText: trimmedSearchText,
        uid,
      });
    },
  });
}

export function useUserProfileSearch({
  enabled = true,
  searchText,
}: {
  enabled?: boolean;
  searchText: string;
}) {
  const trimmedSearchText = searchText.trim();
  const searchableText = trimmedSearchText.replace(/^@/, '');
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedSearchText);

  return useQuery({
    queryKey: userProfileSearchQueryKey(trimmedSearchText),
    enabled: enabled && (looksLikeEmail || searchableText.trim().length >= 2),
    staleTime: 30_000,
    queryFn: async () => {
      return searchUserProfiles({
        limitCount: 20,
        searchText: trimmedSearchText,
      });
    },
  });
}

export function useFollowState(
  currentUid: string | undefined,
  targetUid: string | undefined,
) {
  const queryClient = useQueryClient();
  const queuedFollowing = useQueuedFollowing({ targetUid, uid: currentUid });
  const optimisticQuery = useQuery({
    queryKey: followStateQueryKey(currentUid, targetUid),
    enabled: false,
    initialData: null as boolean | null,
  });
  const [state, setState] = useState({
    error: null as Error | null,
    following: false,
    loading: Boolean(currentUid && targetUid && currentUid !== targetUid),
  });

  useEffect(() => {
    if (!currentUid || !targetUid || currentUid === targetUid) {
      setState({ error: null, following: false, loading: false });
      return undefined;
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: true,
    }));

    return subscribeToFollowState({
      currentUid,
      targetUid,
      onError: (error) => {
        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        }));
      },
      onNext: (following) => {
        queryClient.setQueryData(
          followStateQueryKey(currentUid, targetUid),
          null,
        );
        setState({ error: null, following, loading: false });
      },
    });
  }, [currentUid, queryClient, targetUid]);

  const renderedFollowing =
    queuedFollowing ?? optimisticQuery.data ?? state.following;

  return {
    ...state,
    following: renderedFollowing,
    loading:
      state.loading &&
      queuedFollowing === null &&
      optimisticQuery.data === null,
  };
}

export function useToggleFollow(
  currentUid: string | undefined,
  targetUid: string | undefined,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input?: {
      following?: boolean;
    }): Promise<'queued' | 'saved'> => {
      assert(currentUid, 'User ID is required to follow a profile.');
      assert(targetUid, 'Target user ID is required to follow a profile.');

      if (
        typeof input?.following === 'boolean' &&
        !(await isNetworkAvailable())
      ) {
        await queueSetFollowing({
          currentUid,
          following: input.following,
          targetUid,
        });
        return 'queued';
      }

      try {
        const writePromise = setFollow({
          currentUid,
          following: input?.following,
          targetUid,
        });
        void writePromise.catch(() => undefined);
        await withFollowWriteTimeout(writePromise);
      } catch (error) {
        if (
          typeof input?.following === 'boolean' &&
          isRecoverableNetworkError(error)
        ) {
          await queueSetFollowing({
            currentUid,
            following: input.following,
            targetUid,
          });
          return 'queued';
        }

        throw error;
      }

      return 'saved';
    },
    onMutate: async (input) => {
      const queryKey = followStateQueryKey(currentUid, targetUid);
      const previousFollowing =
        queryClient.getQueryData<boolean | null>(queryKey) ?? null;
      const previousCurrentStats = queryClient.getQueryData<ProfileStats>(
        profileStatsQueryKey(currentUid),
      );
      const previousTargetStats = queryClient.getQueryData<ProfileStats>(
        publicProfileStatsQueryKey(targetUid),
      );
      const previousTargetProfile = queryClient.getQueryData<PublicProfile>(
        publicProfileQueryKey(targetUid),
      );

      if (typeof input?.following === 'boolean') {
        const delta = input.following ? 1 : -1;

        queryClient.setQueryData(queryKey, input.following);
        queryClient.setQueryData<ProfileStats | undefined>(
          profileStatsQueryKey(currentUid),
          (currentStats) =>
            currentStats
              ? {
                  ...currentStats,
                  followingCount: Math.max(
                    0,
                    currentStats.followingCount + delta,
                  ),
                }
              : currentStats,
        );
        queryClient.setQueryData<ProfileStats | undefined>(
          publicProfileStatsQueryKey(targetUid),
          (currentStats) =>
            currentStats
              ? {
                  ...currentStats,
                  followerCount: Math.max(
                    0,
                    currentStats.followerCount + delta,
                  ),
                }
              : currentStats,
        );
        queryClient.setQueryData<PublicProfile | undefined>(
          publicProfileQueryKey(targetUid),
          (currentProfile) =>
            currentProfile
              ? {
                  ...currentProfile,
                  followerCount: Math.max(
                    0,
                    currentProfile.followerCount + delta,
                  ),
                }
              : currentProfile,
        );
      }

      return {
        previousCurrentStats,
        previousFollowing,
        previousTargetProfile,
        previousTargetStats,
      };
    },
    onError: (_error, _input, context) => {
      queryClient.setQueryData(
        followStateQueryKey(currentUid, targetUid),
        context?.previousFollowing ?? null,
      );
      queryClient.setQueryData(
        profileStatsQueryKey(currentUid),
        context?.previousCurrentStats,
      );
      queryClient.setQueryData(
        publicProfileStatsQueryKey(targetUid),
        context?.previousTargetStats,
      );
      queryClient.setQueryData(
        publicProfileQueryKey(targetUid),
        context?.previousTargetProfile,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: profileStatsQueryKey(currentUid),
        }),
        queryClient.invalidateQueries({
          queryKey: publicProfileStatsQueryKey(currentUid),
        }),
        queryClient.invalidateQueries({
          queryKey: publicProfileStatsQueryKey(targetUid),
        }),
      ]);
    },
  });
}

export function useBlockUser(
  currentUid: string | undefined,
  targetUid: string | undefined,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      assert(currentUid, 'User ID is required to block a profile.');
      assert(targetUid, 'Target user ID is required to block a profile.');
      await blockUser(targetUid);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: blockStateQueryKey(currentUid, targetUid),
        }),
        queryClient.invalidateQueries({
          queryKey: blockedUsersQueryKey(currentUid),
        }),
        queryClient.invalidateQueries({
          queryKey: followStateQueryKey(currentUid, targetUid),
        }),
        queryClient.invalidateQueries({
          queryKey: profileStatsQueryKey(currentUid),
        }),
        queryClient.invalidateQueries({
          queryKey: publicProfileStatsQueryKey(targetUid),
        }),
        queryClient.invalidateQueries({
          queryKey: publicProfileQueryKey(targetUid),
        }),
      ]);
    },
  });
}

export function useUnblockUser(
  currentUid: string | undefined,
  targetUid: string | undefined,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      assert(currentUid, 'User ID is required to unblock a profile.');
      assert(targetUid, 'Target user ID is required to unblock a profile.');
      await unblockUser(targetUid);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: blockStateQueryKey(currentUid, targetUid),
        }),
        queryClient.invalidateQueries({
          queryKey: blockedUsersQueryKey(currentUid),
        }),
        queryClient.invalidateQueries({
          queryKey: publicProfileStatsQueryKey(targetUid),
        }),
        queryClient.invalidateQueries({
          queryKey: publicProfileQueryKey(targetUid),
        }),
      ]);
    },
  });
}
